import { useEffect, useState } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom';
import { Box, Typography, Stack, Button, GlobalStyles } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import {
  InfoSnackbar,
  SuccessSnackbar,
  WarningSnackbar,
} from './components/EditApiKeyForm';
import { getApiKeysBySession } from './api/magicLinkApi';
import CreateApiKeyForm from './components/CreateApiKeyForm';
import MagicLinkRequestForm from './components/MagicLinkRequestForm';
import ActivatePage from './components/ActivatePage';
import KeysPage from './components/KeysPage';
import KeyDetailPage from './components/KeyDetailPage';
import Layout from './components/layout';
import useTexts from './hooks/useTexts';

function HomePage() {
  const texts = useTexts();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = searchParams.get('token');

    if (token) {
      navigate(`/activate?token=${encodeURIComponent(token)}`, {
        replace: true,
      });
      return;
    }

    // Zkusíme ověřit session voláním API (protože cookie může být HttpOnly)
    getApiKeysBySession()
      .then(() => {
        navigate('/keys', { replace: true });
      })
      .catch(() => {
        // Session není platná, zůstaneme na landing page
      });
  }, []);

  return (
    <Box sx={{ textAlign: 'left', mt: 8, mb: 4, width: 559 }}>
      <Typography
        color="textPrimary"
        gutterBottom
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 700,
          fontSize: '28px',
          lineHeight: '25px',
          letterSpacing: 0,
        }}
      >
        {texts.PAGE_TITLE}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 400,
          fontSize: '18px',
          lineHeight: '25px',
          letterSpacing: 0,
          color: '#495057',
          mb: 3,
        }}
      >
        {texts.LANDING_SUBTITLE}
      </Typography>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="left"
        mt={5}
      >
        <Button
          component={RouterLink}
          to="/edit"
          variant="contained"
          color="secondary"
          className="landing-action-button"
        >
          {texts.EDIT_API_KEY}
        </Button>
        <Button
          component={RouterLink}
          to="/create"
          variant="contained"
          color="primary"
          className="landing-action-button"
        >
          {texts.CREATE_API_KEY}
        </Button>
      </Stack>
    </Box>
  );
}

function CreatePage() {
  return <CreateApiKeyForm />;
}

function EditPage() {
  return <MagicLinkRequestForm />;
}

function ActivateRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [token] = useState(() => searchParams.get('token'));

  useEffect(() => {
    if (searchParams.has('token')) {
      setSearchParams({}, { replace: true });
    }
  }, []);

  return <ActivatePage token={token} />;
}

function App() {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      Components={{
        info: InfoSnackbar,
        success: SuccessSnackbar,
        warning: WarningSnackbar,
      }}
    >
      <GlobalStyles
        styles={{
          '.notistack-SnackbarContainer': {
            top: '82px !important',
            right: '16px !important',
          },
          '.notistack-SnackbarContainer .notistack-CollapseWrapper': {
            padding: '4px 0px',
          },
        }}
      />
      <Layout>
        <Routes>
          <Route
            path="/"
            element={
              <Box sx={{ maxWidth: 800, mx: 'auto', pt: 4, pb: 8, px: 2 }}>
                <HomePage />
              </Box>
            }
          />
          <Route
            path="/create"
            element={
              <Box sx={{ maxWidth: 1067, mx: 'auto', pt: 4, pb: 8, px: 2 }}>
                <CreatePage />
              </Box>
            }
          />
          <Route
            path="/edit"
            element={
              <Box sx={{ maxWidth: 1067, mx: 'auto', pt: 4, pb: 8, px: 2 }}>
                <EditPage />
              </Box>
            }
          />
          <Route
            path="/activate"
            element={
              <Box sx={{ maxWidth: 1067, mx: 'auto', pt: 4, pb: 8, px: 2 }}>
                <ActivateRoute />
              </Box>
            }
          />
          <Route
            path="/keys"
            element={
              <Box sx={{ maxWidth: 1067, mx: 'auto', pt: 4, pb: 8, px: 2 }}>
                <KeysPage />
              </Box>
            }
          />
          <Route
            path="/keys/detail"
            element={
              <Box
                sx={{
                  maxWidth: '100%',
                  mx: 'auto',
                  pt: 4,
                  pb: { xs: 0, md: 8 },
                  px: 2,
                }}
              >
                <KeyDetailPage />
              </Box>
            }
          />
        </Routes>
      </Layout>
    </SnackbarProvider>
  );
}

export default App;
